/**
 * IpamVlanQuickCreate — alta de VLAN sin salir de la subred.
 *
 * Se abre desde el desplegable de VLAN del formulario de subred (edición y
 * asistente de creación). A la izquierda el alta, a la derecha el **mapa de
 * ocupación**: qué números de VLAN están cogidos, con qué nombre/site y a qué
 * subred(es) pertenecen, para no tener que ir a la pantalla de VLANs a mirarlo.
 *
 * La restricción única en BD es (company_id, vlan_id, site), así que un mismo
 * número puede repetirse en sites distintos: el aviso de "ocupada" solo salta
 * cuando coincide número **y** site (sin site cuenta como un site propio).
 *
 * Props:
 *   companyId     — empresa activa
 *   vlans         — [{ id, vlan_id, name, site, description, subnets:[{cidr,name}] }]
 *   defaultSite   — site sugerido (p. ej. el de la subred que se está creando)
 *   defaultNumber — número sugerido
 *   onCreated(v)  — recibe la VLAN creada (ya con su id de fila) para asociarla
 *   onClose()
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';

const SUGGESTED_NUMBERS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

export default function IpamVlanQuickCreate({
  companyId,
  vlans = [],
  defaultSite = '',
  defaultNumber = '',
  onCreated,
  onClose,
}) {
  const [number, setNumber] = useState(String(defaultNumber || ''));
  const [name, setName]     = useState('');
  const [site, setSite]     = useState(defaultSite || '');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const numberRef = useRef(null);

  useEffect(() => { numberRef.current?.focus(); }, []);

  const normSite = (v) => (v || '').trim() || null;

  // Mapa: número -> [{ vlan, subnets }] agrupado por site
  const map = useMemo(() => {
    const rows = [...(vlans || [])];
    rows.sort((a, b) => (a.vlan_id - b.vlan_id) || String(a.site || '').localeCompare(String(b.site || '')));
    return rows;
  }, [vlans]);

  const typed = useMemo(() => {
    const n = Number(number);
    if (!number || Number.isNaN(n)) return null;
    const s = normSite(site);
    return map.find(v => Number(v.vlan_id) === n && normSite(v.site) === s) || null;
  }, [number, site, map]);

  const nextFree = useMemo(() => {
    const s = normSite(site);
    const usedHere = new Set(map.filter(v => normSite(v.site) === s).map(v => Number(v.vlan_id)));
    const suggested = SUGGESTED_NUMBERS.find(n => !usedHere.has(n));
    if (suggested != null) return suggested;
    for (let n = 1; n <= 4094; n++) if (!usedHere.has(n)) return n;
    return null;
  }, [map, site]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    const n = Number(number);
    if (!number || Number.isNaN(n)) { setError('Indica el número de VLAN.'); return; }
    if (n < 1 || n > 4094)          { setError('La VLAN debe estar entre 1 y 4094.'); return; }
    if (!name.trim())               { setError('El nombre es obligatorio.'); return; }
    if (typed)                      { setError(`La VLAN ${n} ya existe en ese site.`); return; }

    setSaving(true);
    try {
      const res = await api.createIpamVlan(
        { vlan_id: n, name: name.trim(), site: normSite(site), description: description.trim() || null },
        companyId,
      );
      const created = res?.data || res;
      onCreated?.(created);
    } catch (err) {
      setError(err.message || 'No se pudo crear la VLAN');
    } finally {
      setSaving(false);
    }
  };

  const numberTakenHere = map.filter(v => normSite(v.site) === normSite(site));

  return (
    <div
      className="modal d-block"
      style={{ background: 'rgba(15,23,42,.55)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content shadow-lg">
          <div className="modal-header py-2">
            <h6 className="modal-title fw-semibold">🏷️ Nueva VLAN</h6>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <div className="row g-3">
              {/* ── Alta ── */}
              <div className="col-lg-5">
                <form onSubmit={handleSubmit}>
                  <div className="mb-2">
                    <label className="form-label small fw-semibold mb-1">VLAN ID *</label>
                    <input
                      ref={numberRef}
                      className="form-control form-control-sm"
                      type="number"
                      min="1"
                      max="4094"
                      placeholder="100"
                      value={number}
                      onChange={e => setNumber(e.target.value)}
                    />
                    {nextFree != null && (
                      <div className="form-text" style={{ fontSize: '0.75em' }}>
                        Siguiente libre en este site:{' '}
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 align-baseline"
                          style={{ fontSize: '0.95em' }}
                          onClick={() => setNumber(String(nextFree))}
                        >
                          VLAN {nextFree}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mb-2">
                    <label className="form-label small fw-semibold mb-1">Nombre *</label>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Corp-Users"
                      value={name}
                      onChange={e => setName(e.target.value)}
                    />
                  </div>

                  <div className="mb-2">
                    <label className="form-label small fw-semibold mb-1">Site</label>
                    <input
                      className="form-control form-control-sm"
                      placeholder="(sin site)"
                      value={site}
                      onChange={e => setSite(e.target.value)}
                    />
                    <div className="form-text" style={{ fontSize: '0.75em' }}>
                      El mismo número puede repetirse en sites distintos.
                    </div>
                  </div>

                  <div className="mb-2">
                    <label className="form-label small fw-semibold mb-1">Descripción</label>
                    <input
                      className="form-control form-control-sm"
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                    />
                  </div>

                  {typed && (
                    <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: '0.82em' }}>
                      <strong>VLAN {typed.vlan_id} ocupada</strong>
                      {typed.site ? ` en el site «${typed.site}»` : ' sin site'}: «{typed.name}».
                      {typed.subnets?.length > 0 && (
                        <div className="mt-1">
                          La usan: {typed.subnets.map(s => s.cidr).join(', ')}
                        </div>
                      )}
                    </div>
                  )}

                  {error && <div className="alert alert-danger py-2 px-3 mb-2" style={{ fontSize: '0.82em' }}>{error}</div>}

                  <div className="d-flex gap-2 justify-content-end">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={saving}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-success btn-sm" disabled={saving || !!typed}>
                      {saving ? '…' : '＋ Crear y asociar'}
                    </button>
                  </div>
                </form>
              </div>

              {/* ── Mapa de ocupación ── */}
              <div className="col-lg-7">
                <div className="d-flex align-items-center mb-2 gap-2">
                  <span className="fw-semibold small">Mapa de VLANs en uso</span>
                  <span className="badge text-bg-secondary" style={{ fontSize: '0.68em' }}>
                    {map.length} en total · {numberTakenHere.length} en este site
                  </span>
                </div>
                <div
                  className="border rounded"
                  style={{ maxHeight: 340, overflow: 'auto', borderColor: 'var(--border)' }}
                >
                  <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.82em' }}>
                    <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                      <tr>
                        <th style={{ width: 80 }}>VLAN</th>
                        <th>Nombre</th>
                        <th style={{ width: 90 }}>Site</th>
                        <th>Subred(es)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {map.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-muted text-center py-3">
                            No hay VLANs todavía en esta empresa.
                          </td>
                        </tr>
                      )}
                      {map.map(v => {
                        const mine = normSite(v.site) === normSite(site);
                        const isTyped = typed && typed.id === v.id;
                        return (
                          <tr
                            key={v.id}
                            style={isTyped ? { background: 'rgba(245,158,11,.18)' } : undefined}
                          >
                            <td>
                              <span className={`badge text-bg-${mine ? 'primary' : 'secondary'}`} style={{ fontFamily: 'monospace' }}>
                                {v.vlan_id}
                              </span>
                            </td>
                            <td className="text-truncate" style={{ maxWidth: 160 }}>{v.name}</td>
                            <td className="text-muted">{v.site || '—'}</td>
                            <td>
                              {(v.subnets || []).length === 0
                                ? <span className="text-muted">libre</span>
                                : (v.subnets || []).map(s => (
                                  <span key={s.id} className="badge text-bg-info me-1" style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>
                                    {s.cidr}
                                  </span>
                                ))}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="form-text mt-2" style={{ fontSize: '0.75em' }}>
                  «Libre» = la VLAN existe pero ninguna subred la usa todavía.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
